import { commitmentBucket, waitingSince } from './ordering';
import type { CommitmentSummary, Dashboard, DashboardNotice, LaunchedCommitment } from './types';

/**
 * Pulpit „Kafle" (wariant R1, decyzja 60) — **czysta logika doboru pozycji**.
 *
 * Pulpit jest widokiem **informacyjnym**, którego głównym zadaniem jest przeniesienie
 * do właściwego miejsca (decyzja 33). Kafel odpowiada więc na jedno pytanie: *czy warto
 * tam wchodzić* — i robi to podglądem **czterech** pozycji.
 *
 * ⭐ Sedno tego pliku to decyzja 66 (B4): *„cztery pozycje, które w sensie TEJ listy
 * najprędzej wymagają uwagi"*. Sześć list ma **sześć różnych reguł** i to jest świadomy
 * koszt — jedna wspólna („gdzie się pali", jak w drzewku) dla „co uruchomiłem" nie ma sensu.
 */

/** Ile pozycji pokazuje kafel (decyzja 33). Gdy lista ma ich mniej, pokazujemy wszystkie. */
export const TILE_PREVIEW = 4;

/** Identyfikatory kafli. Kolejność tablicy = kolejność na ekranie. */
export const TILE_ORDER = [
  'hanging',
  'fired',
  'dormant',
  'iteration',
  'draugr',
  'notices',
  'launched',
] as const;

export type TileId = (typeof TILE_ORDER)[number];

export interface TileItem {
  key: string;
  title: string;
  /** Druga linia: „wisi od", „czeka na: …", nazwa elementu — zależnie od kafla. */
  meta: TileMeta;
  /** Dokąd prowadzi kliknięcie. Element z otwartym panelem zobowiązań (E1). */
  hostElementId: number;
  /** Wyłącznie kafel „Do wiadomości" — jedyna akcja pulpitu (decyzja 66, B3). */
  noticeId?: number;
}

/**
 * Opis drugiej linii — **klucz i18n plus wartości**, nie gotowe zdanie.
 *
 * Tłumaczenie należy do komponentu (spec 4C: cała treść widoczna dla człowieka idzie
 * przez i18n), a ta warstwa ma zostać testowalna bez montowania Reacta.
 */
export interface TileMeta {
  key: string;
  values?: Record<string, string | number>;
}

export interface Tile {
  id: TileId;
  /** Pełna liczba pozycji na liście — nie liczba pokazanych. */
  count: number;
  items: TileItem[];
  /** Czy kafel woła głośniej niż reszta: martwe surty i obudzone. */
  urgent: boolean;
}

// ── Reguły „które cztery" (decyzja 66, B4) ──────────────────────────────────

/** Najstarsze na górze — po tym, od kiedy pozycja wymaga uwagi. */
function oldestFirst(a: CommitmentSummary, b: CommitmentSummary): number {
  return waitingSince(a).localeCompare(waitingSince(b)) || a.id - b.id;
}

/** Najnowsze na górze — odwrotność powyższego. */
function newestFirst(a: CommitmentSummary, b: CommitmentSummary): number {
  return waitingSince(b).localeCompare(waitingSince(a)) || b.id - a.id;
}

// ── Pełne listy (ekrany z menu) ─────────────────────────────────────────────
//
// ⭐ Kafel pulpitu i ekran listy pokazują **to samo, w tej samej kolejności** — kafel
// jest po prostu przycięty. Dlatego porządek mieszka tutaj, raz: gdyby lista sortowała
// się inaczej niż jej podgląd, „pierwsze cztery" na pulpicie przestałyby być pierwszymi
// czterema na liście, a to psuje zaufanie do obu miejsc naraz.

/** „Zadania na mnie" — czynne bez zapalnika, najdłużej wiszące u góry. */
export function hangingList(dashboard: Dashboard): CommitmentSummary[] {
  return [...dashboard.hanging].sort(oldestFirst);
}

/** „Obudzone surty" — najnowsze u góry, bo obudzone jest przede wszystkim NOWĄ informacją. */
export function firedList(dashboard: Dashboard): CommitmentSummary[] {
  return [...dashboard.freshlyFired].sort(newestFirst);
}

/**
 * Rozdzielenie strukturalne „Czeka na surt" od „Czeka na iterację" (decyzja 92) — po
 * **wyzwalaczu**, nie po opisie. Zadanie ze wskazanym źródłem czeka na cudze domknięcie,
 * nawet jeśli ma też iterację (oba warunki muszą być spełnione, spec 3.4) — surt jest tam
 * rzadszą, nazwaną zależnością. Zadanie z **samą** iteracją czeka wyłącznie na odcięcie
 * iteracji elementu — i to jest osobna sytuacja z osobną drogą wyjścia („Zacznij iterację").
 */
function waitsForIterationOnly(row: CommitmentSummary): boolean {
  return row.surtSourceCommitmentId === null && row.iterationNumber !== null;
}

/** „Czeka na surt" — bez osieroconych i bez czekających tylko na iterację; najdłużej śpiące u góry. */
export function dormantList(dashboard: Dashboard): CommitmentSummary[] {
  return dashboard.dormant
    .filter((row) => commitmentBucket(row) !== 'draugr' && !waitsForIterationOnly(row))
    .sort(oldestFirst);
}

/**
 * „Czeka na iterację" (decyzja 92) — śpiące wyłącznie na odcięcie iteracji swojego elementu.
 * Bez tej listy zadanie kampanii w trybie „iteracja" nie miałoby ŻADNEGO miejsca, w którym
 * właściciel widzi, że coś na jego iterację czeka — a rollup kampanii świadomie tego nie
 * rozbija (rozważone i odłożone przy tej samej decyzji).
 */
export function iterationList(dashboard: Dashboard): CommitmentSummary[] {
  return dashboard.dormant
    .filter((row) => commitmentBucket(row) !== 'draugr' && waitsForIterationOnly(row))
    .sort(oldestFirst);
}

/**
 * „Martwe surty" — osierocone przez cudze zamknięcie.
 *
 * ⭐ Jedyna lista, na której **każda pozycja wymaga decyzji człowieka**: system nie zamknie
 * ich sam (decyzja 21), a osierocenie cofa się tylko przez ponowne otwarcie źródła.
 */
export function draugrList(dashboard: Dashboard): CommitmentSummary[] {
  return dashboard.dormant.filter((row) => commitmentBucket(row) === 'draugr').sort(oldestFirst);
}

/** „Do wiadomości" — nieprzeczytane, najnowsze u góry. */
export function noticeList(dashboard: Dashboard): DashboardNotice[] {
  return [...dashboard.notices].sort(
    (a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc) || b.id - a.id,
  );
}

/**
 * „Co uruchomiłem" — **bez grupowania**, inaczej niż kafel.
 *
 * Kafel odpowiada na „ile po mnie zostało" i grupuje po sprawcy, żeby zmieścić się
 * w czterech wierszach. Ekran odpowiada na inne pytanie — *co konkretnie się rusza* —
 * więc pokazuje pojedyncze zobowiązania, każde z własnym stanem.
 */
export function launchedList(dashboard: Dashboard): LaunchedCommitment[] {
  return [...dashboard.launched].sort(
    (a, b) => b.launchedAtUtc.localeCompare(a.launchedAtUtc) || b.commitment.id - a.commitment.id,
  );
}

function elementMeta(commitment: CommitmentSummary): TileMeta {
  return { key: 'commitments.tile.meta.element', values: { element: commitment.hostElementTitle } };
}

/** Dlaczego to śpi: czeka na cudze zadanie albo na iterację. */
function dormantMeta(commitment: CommitmentSummary): TileMeta {
  if (commitment.surtSourceTitle !== null) {
    return { key: 'commitments.tile.meta.waitingFor', values: { source: commitment.surtSourceTitle } };
  }

  if (commitment.iterationNumber !== null) {
    return { key: 'commitments.tile.meta.iteration', values: { number: commitment.iterationNumber } };
  }

  return elementMeta(commitment);
}

function toItem(commitment: CommitmentSummary, meta: TileMeta): TileItem {
  return {
    key: `c-${commitment.id}`,
    title: commitment.title,
    meta,
    hostElementId: commitment.hostElementId,
  };
}

/**
 * Kafel „Co uruchomiłem" — **grupowany po sprawcy**, nie po zobowiązaniu.
 *
 * Jedno zamknięcie budzi zwykle kilka zadań naraz; sześć wierszy z tą samą przyczyną
 * odpowiadałoby na pytanie, którego nikt nie zadał. Interesuje mnie „co po mnie zostało",
 * czyli **ile** obudziłem i **czy ktoś to ruszył**.
 *
 * Reguła B4 mówi „bez odpowiedzi", więc grupy, w których nic jeszcze nie zamknięto,
 * idą pierwsze — reszta jest tylko potwierdzeniem, że sprawa się domknęła.
 */
function launchedItems(launched: readonly LaunchedCommitment[]): TileItem[] {
  const groups = new Map<string, { title: string; hostElementId: number; total: number; open: number; at: string }>();

  for (const row of launched) {
    // Brak sprawcy nie powinien się zdarzyć, ale gdyby — wiersz ma zostać widoczny,
    // a nie zniknąć w grupie `null` sklejonej z niepowiązanymi zdarzeniami.
    const key = row.causeCommitmentId === null ? `x-${row.commitment.id}` : `g-${row.causeCommitmentId}`;
    const current = groups.get(key);
    const stillOpen = !row.commitment.done && row.commitment.valid ? 1 : 0;

    if (current) {
      current.total += 1;
      current.open += stillOpen;
      current.at = current.at.localeCompare(row.launchedAtUtc) >= 0 ? current.at : row.launchedAtUtc;
    } else {
      groups.set(key, {
        title: row.causeTitle ?? row.commitment.title,
        hostElementId: row.commitment.hostElementId,
        total: 1,
        open: stillOpen,
        at: row.launchedAtUtc,
      });
    }
  }

  return [...groups.entries()]
    .sort(([, a], [, b]) => Number(b.open > 0) - Number(a.open > 0) || b.at.localeCompare(a.at))
    .map(([key, group]) => ({
      key,
      title: group.title,
      meta: {
        key: group.open > 0 ? 'commitments.tile.meta.launchedOpen' : 'commitments.tile.meta.launchedDone',
        values: { count: group.total },
      },
      hostElementId: group.hostElementId,
    }));
}

function noticeItems(notices: readonly DashboardNotice[]): TileItem[] {
  return [...notices]
    .sort((a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc) || b.id - a.id)
    .map((notice) => ({
      key: `n-${notice.id}`,
      title: notice.commitment.title,
      meta: {
        key: `commitments.tile.meta.notice.${notice.kind}`,
        values: { source: notice.causeTitle ?? '' },
      },
      hostElementId: notice.commitment.hostElementId,
      noticeId: notice.id,
    }));
}

/**
 * Sześć kafli w kolejności **zgodnej z menu** (decyzja 66).
 *
 * ⚠ Makieta R1 układa je „najpilniejsze najpierw" i decyzja 66 świadomie to **odrzuca**:
 * *„zaleta R1 to odruch — kafel zawsze w tym samym rogu — a sortowanie po pilności sprawia,
 * że kafle skaczą między wizytami i odruch ginie"*. Pilność niesie licznik i kolor.
 */
export function buildTiles(dashboard: Dashboard): Tile[] {
  // Wszystkie listy biorą się z akcesorów wyżej, więc kafel jest **przyciętą listą**,
  // a nie drugim sortowaniem tych samych danych.
  const hanging = hangingList(dashboard);
  const fired = firedList(dashboard);
  const sleeping = dormantList(dashboard);
  const iteration = iterationList(dashboard);
  const draugr = draugrList(dashboard);

  const tiles: Record<TileId, Tile> = {
    hanging: {
      id: 'hanging',
      count: hanging.length,
      items: hanging.map((c) => toItem(c, ageMeta(c))),
      urgent: false,
    },

    fired: {
      id: 'fired',
      count: fired.length,
      items: fired.map((c) => toItem(c, firedMeta(c))),
      urgent: fired.length > 0,
    },

    dormant: {
      id: 'dormant',
      count: sleeping.length,
      items: sleeping.map((c) => toItem(c, dormantMeta(c))),
      urgent: false,
    },

    iteration: {
      id: 'iteration',
      count: iteration.length,
      items: iteration.map((c) => toItem(c, dormantMeta(c))),
      urgent: false,
    },

    // ⭐ Tu reguła brzmi „wszystkie": martwych surtów jest mało, a każdy wymaga decyzji
    // człowieka (decyzja 21 — system nie zamknie ich sam). Przycięcie do czterech
    // chowałoby pozycje, których nikt poza właścicielem nie ruszy.
    draugr: {
      id: 'draugr',
      count: draugr.length,
      items: draugr.map((c) => toItem(c, elementMeta(c))),
      urgent: draugr.length > 0,
    },

    notices: {
      id: 'notices',
      count: dashboard.notices.length,
      items: noticeItems(dashboard.notices),
      urgent: false,
    },

    launched: {
      id: 'launched',
      count: dashboard.launched.length,
      items: launchedItems(dashboard.launched),
      urgent: false,
    },
  };

  return TILE_ORDER.map((id) => tiles[id]);
}

/** Pozycje do pokazania — „wszystkie" dla martwych surtów, cztery dla reszty. */
export function previewOf(tile: Tile): TileItem[] {
  return tile.id === 'draugr' ? tile.items : tile.items.slice(0, TILE_PREVIEW);
}

function ageMeta(commitment: CommitmentSummary): TileMeta {
  return { key: 'commitments.tile.meta.since', values: { at: waitingSince(commitment) } };
}

function firedMeta(commitment: CommitmentSummary): TileMeta {
  return commitment.surtSourceTitle !== null
    ? { key: 'commitments.tile.meta.firedBy', values: { source: commitment.surtSourceTitle } }
    : elementMeta(commitment);
}

/**
 * Pory dnia, na które dzieli się powitanie. Kolejność = kolejność doby.
 *
 * ⚠ Zbiór jest wypisany, bo z niego składa się klucz i18n (`dashboard.greeting.<pora>`),
 * a takich kluczy skan literałów `t('…')` nie widzi — pilnuje ich strażnik przy zbiorze.
 */
export const GREETING_PARTS = ['morning', 'afternoon', 'evening'] as const;

export type GreetingPart = (typeof GREETING_PARTS)[number];

/**
 * Która pora dnia dla powitania na pulpicie.
 *
 * ⚠⚠ Powstało ze znaleziska ze zrzutów na witrynę (2026-08-18): pulpit witał „Good morning"
 * **o każdej porze**, także o 19:00. Zdanie, które jest pierwszą rzeczą na ekranie, mówiło
 * nieprawdę — a nieprawda w powitaniu podważa liczby stojące niżej.
 *
 * ⛔ Nocy nie ma jako osobnej pory: „dobranoc" jest POŻEGNANIEM, więc witanie nim człowieka
 * siadającego do pracy o 2:00 byłoby drugim kłamstwem. Wieczór obejmuje noc.
 *
 * ⚠ Godzina jest LOKALNA (`getHours`) — powitanie dotyczy człowieka przed ekranem, nie serwera.
 *
 * ⚠ Świadomie NIE przez `Intl`: `dayPeriod` daje **nazwę** pory w danym języku („rano",
 * „in the morning"), a nie stabilny klucz — a nam jest potrzebna wartość, po której składa
 * się klucz tłumaczenia i którą da się przetestować bez zgadywania, co powie ICU.
 *
 * ⚠ Pora liczy się przy renderowaniu, więc otwarty przez całą dobę pulpit nie przeskoczy
 * sam o północy. To świadomie za mało, żeby wieszać na tym zegar (spec 4B: nie pokazujemy
 * tego, czego nie mamy) — pierwszy ruch po ekranie i tak przelicza.
 */
export function greetingPart(now: Date): GreetingPart {
  const hour = now.getHours();

  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';

  return 'evening';
}
